import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
// TODO: Use webp as 1st priority for images
import cx from 'classnames';
import { browserName, isDesktop, osName } from 'react-device-detect';

import { ArrowUpIcon, CloseIcon } from 'components/Icon';

import styles from './PermissionModal.module.scss';

type PermissionModalProps = {
  onClose(): void;
};

const instructionsData = {
  ios: {
    chrome: [
      {
        key: 1,
        text: 'Tap on triple dots',
        image: 'triple-dots',
        alt: 'chrome navigation bar, more menu (triple dots) highlighted'
      },
      {
        key: 2,
        text: 'Tap on Reload',
        image: 'reload',
        alt: 'chrome menu, reload button highlighted'
      }
    ],
    safari: [
      {
        key: 1,
        text: 'Tap on Menu (ᴀA)',
        image: 'more-menu',
        alt: 'safari address bar, Menu (aA) highlighted'
      },
      {
        key: 2,
        text: 'Tap Website Settings',
        image: 'website-settings',
        alt: 'safari website menu, Website Settings highlighted'
      },
      {
        key: 3,
        text: 'Tap Microphone or Camera',
        image: 'permissions',
        alt: 'safari website settings, Microphone and Camera highlighted'
      },
      {
        key: 4,
        text: 'Tap "Allow"',
        image: 'allow',
        alt: 'safari Camera permission, Allow highlighted'
      },
      {
        key: 5,
        text: 'Tap "Done"',
        image: 'done',
        alt: 'safari website settings, Done highlighted'
      },
      {
        key: 6,
        text: 'Refresh the Safari tab',
        image: 'reload',
        alt: 'safari address bar, Refresh icon highlighted'
      }
    ]
  },
  android: {
    chrome: [
      {
        key: 1,
        text: 'Tap on Lock icon',
        image: 'lock',
        alt: 'chrome address bar, Lock icon highlighted'
      },
      {
        key: 2,
        text: 'Tap on Permissions',
        image: 'permissions',
        alt: 'chrome website settings, Permissions highlighted'
      },
      {
        key: 3,
        text: 'Toggle Camera and Microphone on',
        image: 'toggle',
        alt: 'chrome website permissions, Camera and Microphone toggles highlighted'
      },
      {
        key: 4,
        text: 'Tap on triple dots',
        image: 'triple-dots',
        alt: 'chrome address bar, more menu (triple dots) highlighted'
      },
      {
        key: 5,
        text: 'Refresh the Chrome tab',
        image: 'reload',
        alt: 'safari address bar, Refresh icon highlighted'
      }
    ]
  },
  desktop: {
    safari: [
      {
        key: 1,
        text: 'Open Website Settings',
        image: 'preferences',
        alt: 'mac os safari menu, settings for this website highlighted'
      },
      {
        key: 2,
        text: 'Tap on Camera or Microphone permission',
        image: 'permissions',
        alt: 'mac os safari website settings, permissions highlighted'
      },
      {
        key: 3,
        text: 'Tap on "Allow"',
        image: 'allow',
        alt: 'mac os safari, Camera and Microphone allow permissions highlighted'
      },
      {
        key: 4,
        text: 'Refresh the Safari tab',
        image: 'refresh',
        alt: 'mac os safari, refresh icon highlighted'
      }
    ],
    chrome: [
      {
        key: 1,
        text: 'Tap on Lock icon',
        image: 'lock',
        alt: 'chrome address bar, Lock icon highlighted'
      },
      {
        key: 2,
        text: 'Toggle Camera and Microphone on',
        image: 'permissions',
        alt: 'chrome website permissions, Camera and Microphone toggles highlighted'
      },
      {
        key: 4,
        text: 'Reload the Chrome tab',
        image: 'reload',
        alt: 'safari address bar, Refresh icon highlighted'
      }
    ]
  }
};

const PermissionModal: FC<PermissionModalProps> = ({ onClose }): JSX.Element => {
  const isIOS = useMemo(() => osName.toLowerCase() === 'ios', []);
  const isAndroid = useMemo(() => osName.toLowerCase() === 'android', []);
  const isChrome = useMemo(() => browserName.toLowerCase() === 'chrome', []);
  const isSafari = useMemo(() => browserName.toLowerCase().includes('safari'), []);

  const renderInstructions = useCallback(() => {
    const getOS = () => {
      if (isIOS) {
        return 'ios';
      }
      if (isAndroid) {
        return 'android';
      }
      if (isDesktop) {
        return 'desktop';
      }
      return null;
    };

    const getBrowser = () => {
      if (isChrome) {
        return 'chrome';
      }
      if (isSafari) {
        return 'safari';
      }
      return null;
    };

    const os = getOS();
    const browser = getBrowser();

    if (!os || !browser) {
      return (
        <>
          <li>Provide permission for Microphone and Camera</li>
          <li>Reload the tab</li>
        </>
      );
    }

    return instructionsData[os][browser].map(item => (
      <li key={item.key}>
        {item.text}{' '}
        <div className={styles.image}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/images/instructions/${os}-${browser}-${item.image}.png`}
            alt={item.alt}
          />
        </div>
      </li>
    ));
  }, [isIOS, isAndroid, isChrome, isSafari]);

  const pointerPosition = useMemo(() => {
    if (isAndroid && isChrome) {
      return 'up-left';
    }

    if (isIOS && isChrome) {
      return 'down-right';
    }

    return null;
  }, []);

  return createPortal(
    <div className={styles.overlay}>
      {pointerPosition && (
        <div
          className={cx(styles.pointer, {
            [styles[`pointer--${pointerPosition}`]]: pointerPosition
          })}
        >
          <ArrowUpIcon fill="#fff" />
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.close} onClick={onClose} role="button" tabIndex={0}>
          <CloseIcon width="20px" height="20px" />
        </div>
        <div className={styles.content}>
          <div className={styles.title}>Your microphone or camera permission is blocked. Follow steps to enable:</div>
          <div className={styles.text}>
            <ol>{renderInstructions()}</ol>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PermissionModal;
